import { AddOnTextInput, Card, ErrorMessage, TextInput, Typography } from "@/components/atoms";
import { useAppSelector } from "@/redux/hooks";
import { waitingTimesSelector } from "@/redux/slices/operator/selectors";
import { WaitTimeFormData, FormRegister, FormErrors } from "../types";

interface WaitTimeCardProps {
  register: FormRegister<WaitTimeFormData>;
  errors: FormErrors<WaitTimeFormData>;
  isHourlyOrDailyRate: boolean;
  additionalWaitingTime?: number;
  additionalRunTime?: number;
  additionalWaitTimeFee?: number;
  additionalRunTimeFee?: number;
  isLoadingFees: boolean;
}

export const WaitTimeCard = ({
  register,
  errors,
  isHourlyOrDailyRate,
  additionalWaitingTime,
  additionalRunTime: _additionalRunTime,
  additionalWaitTimeFee,
  additionalRunTimeFee,
  isLoadingFees,
}: WaitTimeCardProps) => {
  const waitingTimes = useAppSelector(waitingTimesSelector);
  return (
    <Card as="fieldset">
      <Typography variant="title">Wait Time</Typography>

      <Typography variant="action" className="mb-1 mt-3 block text-neutral-dark-gray">
        {isHourlyOrDailyRate ? "Additional Run Time (minutes)" : "Additional Waiting Time (minutes) after the Courtesy Waiting Times"}
      </Typography>

      {isHourlyOrDailyRate ? (
        <div className="grid gap-2.5">
          <div className="flex gap-4">
            <TextInput placeholder="Eg. 10" type="number" className="flex-1" {...register("additionalRunTime", { valueAsNumber: true })} />
            <AddOnTextInput
              value={additionalRunTimeFee}
              startAddOn="AUD $"
              placeholder="0.00"
              className="flex-1"
              tabIndex={-1}
              readOnly
              disabled={isLoadingFees}
            />
          </div>
          <ErrorMessage errors={errors} name="additionalRunTime" />
        </div>
      ) : (
        <div className="grid gap-2">
          <div className="flex gap-2.5">
            <div className="flex-1">
              <TextInput
                placeholder="Eg. 10"
                type="number"
                className="flex-1"
                {...register("additionalWaitingTime", { valueAsNumber: true })}
              />
            </div>
            <div className="flex-1">
              <AddOnTextInput
                value={additionalWaitingTime ? additionalWaitTimeFee : ""}
                startAddOn="AUD $"
                placeholder="0.00"
                tabIndex={-1}
                className="flex-1"
                readOnly
                disabled={isLoadingFees}
              />
            </div>
          </div>
          <ErrorMessage errors={errors} name="additionalWaitingTime" />
        </div>
      )}

      {/* The operator's OWN configured minutes (Config → Trip → Waiting Times), which this sentence
        used to hardcode as 10/30/60 — telling an operator their own courtesy times were the platform
        defaults, on the form where they decide what to charge for waiting beyond them.

        The run-time line is unconditional; the courtesy line renders only once the numbers are known,
        because a default stated confidently is exactly the defect being fixed. */}
      {(isHourlyOrDailyRate || waitingTimes) && (
        <Typography className="mt-1 leading-loose text-neutral-silver-gray">
          {isHourlyOrDailyRate
            ? "Number of minutes of additional run time for hourly / daily jobs."
            : `Courtesy waiting times are: ${waitingTimes!.standard} minutes regular pickup, ${
                waitingTimes!.domesticAirport
              } minutes Domestic Airport, ${waitingTimes!.internationalAirport} minutes International Airport`}
        </Typography>
      )}
    </Card>
  );
};
